/** Named React entry points, including names stored in existing documents.
 * All artwork comes from the shared registry; no SVG copies live here. */
import { createElement, type SVGProps } from 'react';

import type { XuwoIconName } from './data';
import { namedIconNames } from './names';
import { resolveXuwoIcon } from './render';

type IconProps = SVGProps<SVGSVGElement> & { size?: number | string };

const icon = (name: XuwoIconName) => {
  // 不要给 props 加 `= {}` 默认值:那会让 React 的 createElement<P> 把 P 推断成
  // `{}` 而不是 IconProps,于是 `createElement(SomeIcon, { height: '20' })` 报
  // 「'height' does not exist in type 'Attributes'」。曾因此在 tsc -b electron
  // 里一次产生 132 条 TS2769。IconProps 各字段本就可选,React 也总是传 props
  // 对象,去掉默认值不影响 <Icon /> 与 createElement(Icon) 的用法。
  const Icon = ({ size = '1em', ...props }: IconProps) => {
    const data = resolveXuwoIcon(name, size);
    return createElement('svg', {
      xmlns: 'http://www.w3.org/2000/svg',
      viewBox: data.viewBox,
      width: data.width,
      height: data.height,
      fill: data.fill ?? 'currentColor',
      style: { userSelect: 'none', flexShrink: 0 },
      ...props,
      dangerouslySetInnerHTML: { __html: data.content },
    });
  };
  return Icon;
};

export const AccountBanIcon = icon(namedIconNames.AccountBanIcon);
export const AccountEnableIcon = icon(namedIconNames.AccountEnableIcon);
export const AccountIcon = icon(namedIconNames.AccountIcon);
export const AddCollectionIcon = icon(namedIconNames.AddCollectionIcon);
export const AddCommentIcon = icon(namedIconNames.AddCommentIcon);
export const AddCursorDuotoneIcon = icon(namedIconNames.AddCursorDuotoneIcon);
export const AddCursorIcon = icon(namedIconNames.AddCursorIcon);
export const AddOrganizeIcon = icon(namedIconNames.AddOrganizeIcon);
export const AddTagIcon = icon(namedIconNames.AddTagIcon);
export const AddTextIcon = icon(namedIconNames.AddTextIcon);
export const AdminIcon = icon(namedIconNames.AdminIcon);
export const AiEmbeddingIcon = icon(namedIconNames.AiEmbeddingIcon);
export const AiIcon = icon(namedIconNames.AiIcon);
export const AiOutlineIcon = icon(namedIconNames.AiOutlineIcon);
export const AiThinkingIcon = icon(namedIconNames.AiThinkingIcon);
export const AliasIcon = icon(namedIconNames.AliasIcon);
export const AlignBottomIcon = icon(namedIconNames.AlignBottomIcon);
export const AlignHorizontalCenterIcon = icon(
  namedIconNames.AlignHorizontalCenterIcon
);
export const AlignLeftIcon = icon(namedIconNames.AlignLeftIcon);
export const AlignRightIcon = icon(namedIconNames.AlignRightIcon);
export const AlignTopIcon = icon(namedIconNames.AlignTopIcon);
export const AlignVerticalCenterIcon = icon(
  namedIconNames.AlignVerticalCenterIcon
);
export const AllDocsIcon = icon(namedIconNames.AllDocsIcon);
export const AppearanceIcon = icon(namedIconNames.AppearanceIcon);
export const AppleDuotoneIcon = icon(namedIconNames.AppleDuotoneIcon);
export const AppleIcon = icon(namedIconNames.AppleIcon);
export const ArrowDownBigBottomIcon = icon(
  namedIconNames.ArrowDownBigBottomIcon
);
export const ArrowDownBigIcon = icon(namedIconNames.ArrowDownBigIcon);
export const ArrowDownSmallIcon = icon(namedIconNames.ArrowDownSmallIcon);
export const ArrowDownSmallPlusIcon = icon(
  namedIconNames.ArrowDownSmallPlusIcon
);
export const ArrowLeftBigIcon = icon(namedIconNames.ArrowLeftBigIcon);
export const ArrowLeftSmallIcon = icon(namedIconNames.ArrowLeftSmallIcon);
export const ArrowLeftSmallPlusIcon = icon(
  namedIconNames.ArrowLeftSmallPlusIcon
);
export const ArrowRightBigIcon = icon(namedIconNames.ArrowRightBigIcon);
export const ArrowRightSmallIcon = icon(namedIconNames.ArrowRightSmallIcon);
export const ArrowRightSmallPlusIcon = icon(
  namedIconNames.ArrowRightSmallPlusIcon
);
export const ArrowShapeIcon = icon(namedIconNames.ArrowShapeIcon);
export const ArrowUpBigIcon = icon(namedIconNames.ArrowUpBigIcon);
export const ArrowUpBigTopIcon = icon(namedIconNames.ArrowUpBigTopIcon);
export const ArrowUpSmallIcon = icon(namedIconNames.ArrowUpSmallIcon);
export const ArrowUpSmallPlusIcon = icon(namedIconNames.ArrowUpSmallPlusIcon);
export const ArrowsLeftIcon = icon(namedIconNames.ArrowsLeftIcon);
export const ArrowsRightIcon = icon(namedIconNames.ArrowsRightIcon);
export const AtIcon = icon(namedIconNames.AtIcon);
export const AttachmentIcon = icon(namedIconNames.AttachmentIcon);
export const AutoHeightIcon = icon(namedIconNames.AutoHeightIcon);
export const AutoSizeIcon = icon(namedIconNames.AutoSizeIcon);
export const AutoTidyUpIcon = icon(namedIconNames.AutoTidyUpIcon);
export const BackwardPanelIcon = icon(namedIconNames.BackwardPanelIcon);
export const BagPanelIcon = icon(namedIconNames.BagPanelIcon);
export const BanIcon = icon(namedIconNames.BanIcon);
export const BaseballPanelIcon = icon(namedIconNames.BaseballPanelIcon);
export const BasketballPanelIcon = icon(namedIconNames.BasketballPanelIcon);
export const BitcoinPanelIcon = icon(namedIconNames.BitcoinPanelIcon);
export const BlockIcon = icon(namedIconNames.BlockIcon);
export const BlockLinkIcon = icon(namedIconNames.BlockLinkIcon);
export const BlocksuiteIcon = icon(namedIconNames.BlocksuiteIcon);
export const BoldIcon = icon(namedIconNames.BoldIcon);
export const BookPanelIcon = icon(namedIconNames.BookPanelIcon);
export const BookmarkIcon = icon(namedIconNames.BookmarkIcon);
export const BrokenImageIcon = icon(namedIconNames.BrokenImageIcon);
export const BroomIcon = icon(namedIconNames.BroomIcon);
export const BrushIcon = icon(namedIconNames.BrushIcon);
export const BubbleIcon = icon(namedIconNames.BubbleIcon);
export const BulletedList01Icon = icon(namedIconNames.BulletedList01Icon);
export const BulletedList02Icon = icon(namedIconNames.BulletedList02Icon);
export const BulletedList03Icon = icon(namedIconNames.BulletedList03Icon);
export const BulletedList04Icon = icon(namedIconNames.BulletedList04Icon);
export const BulletedListIcon = icon(namedIconNames.BulletedListIcon);
export const CalendarPanelIcon = icon(namedIconNames.CalendarPanelIcon);
export const CalendarXmarkIcon = icon(namedIconNames.CalendarXmarkIcon);
export const CameraIcon = icon(namedIconNames.CameraIcon);
export const CameraPanelIcon = icon(namedIconNames.CameraPanelIcon);
export const CancelWrapIcon = icon(namedIconNames.CancelWrapIcon);
export const CaptionIcon = icon(namedIconNames.CaptionIcon);
export const CardPanelIcon = icon(namedIconNames.CardPanelIcon);
export const Cart2PanelIcon = icon(namedIconNames.Cart2PanelIcon);
export const CartPanelIcon = icon(namedIconNames.CartPanelIcon);
export const CategoryPanelIcon = icon(namedIconNames.CategoryPanelIcon);
export const CenterPeekIcon = icon(namedIconNames.CenterPeekIcon);
export const ChartPanelIcon = icon(namedIconNames.ChartPanelIcon);
export const ChatPanelIcon = icon(namedIconNames.ChatPanelIcon);
export const ChatWithAiIcon = icon(namedIconNames.ChatWithAiIcon);
export const CheckBoxCheckLinearIcon = icon(
  namedIconNames.CheckBoxCheckLinearIcon
);
export const CheckBoxCheckSolidIcon = icon(
  namedIconNames.CheckBoxCheckSolidIcon
);
export const CheckBoxIntermediateIcon = icon(
  namedIconNames.CheckBoxIntermediateIcon
);
export const CheckBoxUnIcon = icon(namedIconNames.CheckBoxUnIcon);
export const ChemicalPanelIcon = icon(namedIconNames.ChemicalPanelIcon);
export const CirclePanelIcon = icon(namedIconNames.CirclePanelIcon);
export const ClaudeDuotoneIcon = icon(namedIconNames.ClaudeDuotoneIcon);
export const ClientIcon = icon(namedIconNames.ClientIcon);
export const CloseIcon = icon(namedIconNames.CloseIcon);
export const CloudBubbleIcon = icon(namedIconNames.CloudBubbleIcon);
export const CloudWorkspaceIcon = icon(namedIconNames.CloudWorkspaceIcon);
export const Cloudy2PanelIcon = icon(namedIconNames.Cloudy2PanelIcon);
export const CloudyPanelIcon = icon(namedIconNames.CloudyPanelIcon);
export const CodeBlockIcon = icon(namedIconNames.CodeBlockIcon);
export const CodeIcon = icon(namedIconNames.CodeIcon);
export const CodePanelIcon = icon(namedIconNames.CodePanelIcon);
export const CoinPanelIcon = icon(namedIconNames.CoinPanelIcon);
export const CollaborationIcon = icon(namedIconNames.CollaborationIcon);
export const CollapseIcon = icon(namedIconNames.CollapseIcon);
export const CollapseTabIcon = icon(namedIconNames.CollapseTabIcon);
export const CollectPanelIcon = icon(namedIconNames.CollectPanelIcon);
export const CollectionsIcon = icon(namedIconNames.CollectionsIcon);
export const ColorPanelIcon = icon(namedIconNames.ColorPanelIcon);
export const ColorPickerIcon = icon(namedIconNames.ColorPickerIcon);
export const CommentDuodoneIcon = icon(namedIconNames.CommentDuodoneIcon);
export const CommentIcon = icon(namedIconNames.CommentIcon);
export const CompressIcon = icon(namedIconNames.CompressIcon);
export const Computer2PanelIcon = icon(namedIconNames.Computer2PanelIcon);
export const ComputerPanelIcon = icon(namedIconNames.ComputerPanelIcon);
export const ConfusePanelIcon = icon(namedIconNames.ConfusePanelIcon);
export const ConnectorCIcon = icon(namedIconNames.ConnectorCIcon);
export const ConnectorEIcon = icon(namedIconNames.ConnectorEIcon);
export const ConnectorLIcon = icon(namedIconNames.ConnectorLIcon);
export const ContactWithUsIcon = icon(namedIconNames.ContactWithUsIcon);
export const ConvertIcon = icon(namedIconNames.ConvertIcon);
export const CopyAsImgaeIcon = icon(namedIconNames.CopyAsImgaeIcon);
export const CopyIcon = icon(namedIconNames.CopyIcon);
export const CornerIcon = icon(namedIconNames.CornerIcon);
export const CreatedIcon = icon(namedIconNames.CreatedIcon);
export const CropIcon = icon(namedIconNames.CropIcon);
export const CrossedCircleIcon = icon(namedIconNames.CrossedCircleIcon);
export const CubePanelIcon = icon(namedIconNames.CubePanelIcon);
export const CurveLineIcon = icon(namedIconNames.CurveLineIcon);
export const CustomSizeIcon = icon(namedIconNames.CustomSizeIcon);
export const CustomizedHeightIcon = icon(namedIconNames.CustomizedHeightIcon);
export const CylinderIcon = icon(namedIconNames.CylinderIcon);
export const DarkModeIcon = icon(namedIconNames.DarkModeIcon);
export const DashLineIcon = icon(namedIconNames.DashLineIcon);
export const Data2PanelIcon = icon(namedIconNames.Data2PanelIcon);
export const DataPanelIcon = icon(namedIconNames.DataPanelIcon);
export const DatabaseKanbanViewIcon = icon(
  namedIconNames.DatabaseKanbanViewIcon
);
export const DatabaseListViewIcon = icon(namedIconNames.DatabaseListViewIcon);
export const DatabaseTableViewIcon = icon(namedIconNames.DatabaseTableViewIcon);
export const DateTimeIcon = icon(namedIconNames.DateTimeIcon);
export const DeleteIcon = icon(namedIconNames.DeleteIcon);
export const DeletePermanentlyIcon = icon(namedIconNames.DeletePermanentlyIcon);
export const DeleteTemporarilyIcon = icon(namedIconNames.DeleteTemporarilyIcon);
export const Diagonally_2LinesIcon = icon(namedIconNames.Diagonally_2LinesIcon);
export const DiamondIcon = icon(namedIconNames.DiamondIcon);
export const DirectionPanelIcon = icon(namedIconNames.DirectionPanelIcon);
export const DirectionSign2PanelIcon = icon(
  namedIconNames.DirectionSign2PanelIcon
);
export const DirectionSign3PanelIcon = icon(
  namedIconNames.DirectionSign3PanelIcon
);
export const DirectionSign4PanelIcon = icon(
  namedIconNames.DirectionSign4PanelIcon
);
export const DirectionSignPanelIcon = icon(
  namedIconNames.DirectionSignPanelIcon
);
export const DiscordDuotoneIcon = icon(namedIconNames.DiscordDuotoneIcon);
export const DiscordIcon = icon(namedIconNames.DiscordIcon);
export const DiscordPanelIcon = icon(namedIconNames.DiscordPanelIcon);
export const DistributeHorizontalIcon = icon(
  namedIconNames.DistributeHorizontalIcon
);
export const DistributeVerticalIcon = icon(
  namedIconNames.DistributeVerticalIcon
);
export const DividerIcon = icon(namedIconNames.DividerIcon);
export const DocsDuotoneIcon = icon(namedIconNames.DocsDuotoneIcon);
export const DocsIcon = icon(namedIconNames.DocsIcon);
export const DoneIcon = icon(namedIconNames.DoneIcon);
export const DotDuotoneIcon = icon(namedIconNames.DotDuotoneIcon);
export const DownloadIcon = icon(namedIconNames.DownloadIcon);
export const DragCursorDuotoneIcon = icon(namedIconNames.DragCursorDuotoneIcon);
export const DragCursorIcon = icon(namedIconNames.DragCursorIcon);
export const Drink2PanelIcon = icon(namedIconNames.Drink2PanelIcon);
export const DrinkPanelIcon = icon(namedIconNames.DrinkPanelIcon);
export const DropCursorDuotoneIcon = icon(namedIconNames.DropCursorDuotoneIcon);
export const DropCursorIcon = icon(namedIconNames.DropCursorIcon);
export const DualLinkIcon = icon(namedIconNames.DualLinkIcon);
export const DuplicateIcon = icon(namedIconNames.DuplicateIcon);
export const EdgelessIcon = icon(namedIconNames.EdgelessIcon);
export const EditIcon = icon(namedIconNames.EditIcon);
export const ElbowedLineIcon = icon(namedIconNames.ElbowedLineIcon);
export const EllipseIcon = icon(namedIconNames.EllipseIcon);
export const EllipsisIcon = icon(namedIconNames.EllipsisIcon);
export const EmailIcon = icon(namedIconNames.EmailIcon);
export const EmailWarningIcon = icon(namedIconNames.EmailWarningIcon);
export const EmbedIcon = icon(namedIconNames.EmbedIcon);
export const EmbedWebIcon = icon(namedIconNames.EmbedWebIcon);
export const EmptyIcon = icon(namedIconNames.EmptyIcon);
export const EndPointArrowIcon = icon(namedIconNames.EndPointArrowIcon);
export const EndPointCircleIcon = icon(namedIconNames.EndPointCircleIcon);
export const EndPointDiamondIcon = icon(namedIconNames.EndPointDiamondIcon);
export const EndPointTriangleIcon = icon(namedIconNames.EndPointTriangleIcon);
export const EnterIcon = icon(namedIconNames.EnterIcon);
export const EqIcon = icon(namedIconNames.EqIcon);
export const EraserIcon = icon(namedIconNames.EraserIcon);
export const ExpandCloseIcon = icon(namedIconNames.ExpandCloseIcon);
export const ExpandFullIcon = icon(namedIconNames.ExpandFullIcon);
export const ExpandIcon = icon(namedIconNames.ExpandIcon);
export const ExpandWideIcon = icon(namedIconNames.ExpandWideIcon);
export const ExperimentIcon = icon(namedIconNames.ExperimentIcon);
export const ExplainIcon = icon(namedIconNames.ExplainIcon);
export const ExportIcon = icon(namedIconNames.ExportIcon);
export const ExportToHtmlIcon = icon(namedIconNames.ExportToHtmlIcon);
export const ExportToMarkdownIcon = icon(namedIconNames.ExportToMarkdownIcon);
export const ExportToPdfIcon = icon(namedIconNames.ExportToPdfIcon);
export const ExportToPngIcon = icon(namedIconNames.ExportToPngIcon);
export const ExportToSvgIcon = icon(namedIconNames.ExportToSvgIcon);
export const EyePanelIcon = icon(namedIconNames.EyePanelIcon);
export const FacebookDuotoneIcon = icon(namedIconNames.FacebookDuotoneIcon);
export const FacebookIcon = icon(namedIconNames.FacebookIcon);
export const FavoriteIcon = icon(namedIconNames.FavoriteIcon);
export const FavoritedIcon = icon(namedIconNames.FavoritedIcon);
export const FigmaDuotoneIcon = icon(namedIconNames.FigmaDuotoneIcon);
export const FigmaIcon = icon(namedIconNames.FigmaIcon);
export const FileIcon = icon(namedIconNames.FileIcon);
export const FileIconAepIcon = icon(namedIconNames.FileIconAepIcon);
export const FileIconAiIcon = icon(namedIconNames.FileIconAiIcon);
export const FileIconAviIcon = icon(namedIconNames.FileIconAviIcon);
export const FileIconCssIcon = icon(namedIconNames.FileIconCssIcon);
export const FileIconCsvIcon = icon(namedIconNames.FileIconCsvIcon);
export const FileIconDmgIcon = icon(namedIconNames.FileIconDmgIcon);
export const FileIconDocIcon = icon(namedIconNames.FileIconDocIcon);
export const FileIconDocxIcon = icon(namedIconNames.FileIconDocxIcon);
export const FileIconEpsIcon = icon(namedIconNames.FileIconEpsIcon);
export const FileIconExeIcon = icon(namedIconNames.FileIconExeIcon);
export const FileIconFigIcon = icon(namedIconNames.FileIconFigIcon);
export const FileIconGifIcon = icon(namedIconNames.FileIconGifIcon);
export const FileIconHtmlIcon = icon(namedIconNames.FileIconHtmlIcon);
export const FileIconImgIcon = icon(namedIconNames.FileIconImgIcon);
export const FileIconInddIcon = icon(namedIconNames.FileIconInddIcon);
export const FileIconJavaIcon = icon(namedIconNames.FileIconJavaIcon);
export const FileIconJpegIcon = icon(namedIconNames.FileIconJpegIcon);
export const FileIconJpgIcon = icon(namedIconNames.FileIconJpgIcon);
export const FileIconJsIcon = icon(namedIconNames.FileIconJsIcon);
export const FileIconJsonIcon = icon(namedIconNames.FileIconJsonIcon);
export const FileIconMdIcon = icon(namedIconNames.FileIconMdIcon);
export const FileIconMkvIcon = icon(namedIconNames.FileIconMkvIcon);
export const FileIconMp3Icon = icon(namedIconNames.FileIconMp3Icon);
export const FileIconMp4Icon = icon(namedIconNames.FileIconMp4Icon);
export const FileIconMpegIcon = icon(namedIconNames.FileIconMpegIcon);
export const FileIconNoneIcon = icon(namedIconNames.FileIconNoneIcon);
export const FileIconPdfIcon = icon(namedIconNames.FileIconPdfIcon);
export const FileIconPngIcon = icon(namedIconNames.FileIconPngIcon);
export const FileIconPptIcon = icon(namedIconNames.FileIconPptIcon);
export const FileIconPptxIcon = icon(namedIconNames.FileIconPptxIcon);
export const FileIconPsdIcon = icon(namedIconNames.FileIconPsdIcon);
export const FileIconRarIcon = icon(namedIconNames.FileIconRarIcon);
export const FileIconRssIcon = icon(namedIconNames.FileIconRssIcon);
export const FileIconSketchIcon = icon(namedIconNames.FileIconSketchIcon);
export const FileIconSqlIcon = icon(namedIconNames.FileIconSqlIcon);
export const FileIconSvgIcon = icon(namedIconNames.FileIconSvgIcon);
export const FileIconTiffIcon = icon(namedIconNames.FileIconTiffIcon);
export const FileIconTxtIcon = icon(namedIconNames.FileIconTxtIcon);
export const FileIconWavIcon = icon(namedIconNames.FileIconWavIcon);
export const FileIconWebpIcon = icon(namedIconNames.FileIconWebpIcon);
export const FileIconXlsIcon = icon(namedIconNames.FileIconXlsIcon);
export const FileIconXlsxIcon = icon(namedIconNames.FileIconXlsxIcon);
export const FileIconXmlIcon = icon(namedIconNames.FileIconXmlIcon);
export const FileIconZipIcon = icon(namedIconNames.FileIconZipIcon);
export const FilePanelIcon = icon(namedIconNames.FilePanelIcon);
export const FilterIcon = icon(namedIconNames.FilterIcon);
export const FilterMinusIcon = icon(namedIconNames.FilterMinusIcon);
export const FilterUndoIcon = icon(namedIconNames.FilterUndoIcon);
export const FilteredIcon = icon(namedIconNames.FilteredIcon);
export const FirePanelIcon = icon(namedIconNames.FirePanelIcon);
export const Flag2PanelIcon = icon(namedIconNames.Flag2PanelIcon);
export const Flag3PanelIcon = icon(namedIconNames.Flag3PanelIcon);
export const FlagPanelIcon = icon(namedIconNames.FlagPanelIcon);
export const FlashPanelIcon = icon(namedIconNames.FlashPanelIcon);
export const FlipDirectionIcon = icon(namedIconNames.FlipDirectionIcon);
export const FlowerPanelIcon = icon(namedIconNames.FlowerPanelIcon);
export const Folder2PanelIcon = icon(namedIconNames.Folder2PanelIcon);
export const Folder3PanelIcon = icon(namedIconNames.Folder3PanelIcon);
export const FolderIcon = icon(namedIconNames.FolderIcon);
export const FolderPanelIcon = icon(namedIconNames.FolderPanelIcon);
export const FontIcon = icon(namedIconNames.FontIcon);
export const FoodPanelIcon = icon(namedIconNames.FoodPanelIcon);
export const ForwardPanelIcon = icon(namedIconNames.ForwardPanelIcon);
export const ForwardThirtySecondsIcon = icon(
  namedIconNames.ForwardThirtySecondsIcon
);
export const FrameIcon = icon(namedIconNames.FrameIcon);
export const FruitPanelIcon = icon(namedIconNames.FruitPanelIcon);
export const FullDayIcon = icon(namedIconNames.FullDayIcon);
export const GamePanelIcon = icon(namedIconNames.GamePanelIcon);
export const GeminiDuotoneIcon = icon(namedIconNames.GeminiDuotoneIcon);
export const GiftPanelIcon = icon(namedIconNames.GiftPanelIcon);
export const GithubDuotoneIcon = icon(namedIconNames.GithubDuotoneIcon);
export const GithubIcon = icon(namedIconNames.GithubIcon);
export const GithubPanelIcon = icon(namedIconNames.GithubPanelIcon);
export const GitlabDuotoneIcon = icon(namedIconNames.GitlabDuotoneIcon);
export const GitlabIcon = icon(namedIconNames.GitlabIcon);
export const GobletPanelIcon = icon(namedIconNames.GobletPanelIcon);
export const GoodIcon = icon(namedIconNames.GoodIcon);
export const GoogleDuotoneIcon = icon(namedIconNames.GoogleDuotoneIcon);
export const GoogleIcon = icon(namedIconNames.GoogleIcon);
export const GoogleMapDuotoneIcon = icon(namedIconNames.GoogleMapDuotoneIcon);
export const GoogleMapIcon = icon(namedIconNames.GoogleMapIcon);
export const GooglePanelIcon = icon(namedIconNames.GooglePanelIcon);
export const GptDuotoneIcon = icon(namedIconNames.GptDuotoneIcon);
export const GridIcon = icon(namedIconNames.GridIcon);
export const GroupIcon = icon(namedIconNames.GroupIcon);
export const GroupPanelIcon = icon(namedIconNames.GroupPanelIcon);
export const GroupingIcon = icon(namedIconNames.GroupingIcon);
export const GtIcon = icon(namedIconNames.GtIcon);
export const HamburgerPanelIcon = icon(namedIconNames.HamburgerPanelIcon);
export const HandIcon = icon(namedIconNames.HandIcon);
export const HappyPanelIcon = icon(namedIconNames.HappyPanelIcon);
export const HeaderColumnIcon = icon(namedIconNames.HeaderColumnIcon);
export const HeaderRowIcon = icon(namedIconNames.HeaderRowIcon);
export const Heading1Icon = icon(namedIconNames.Heading1Icon);
export const Heading2Icon = icon(namedIconNames.Heading2Icon);
export const Heading3Icon = icon(namedIconNames.Heading3Icon);
export const Heading4Icon = icon(namedIconNames.Heading4Icon);
export const Heading5Icon = icon(namedIconNames.Heading5Icon);
export const Heading6Icon = icon(namedIconNames.Heading6Icon);
export const HeadingsIcon = icon(namedIconNames.HeadingsIcon);
export const HeadphonePanelIcon = icon(namedIconNames.HeadphonePanelIcon);
export const HeartPanelIcon = icon(namedIconNames.HeartPanelIcon);
export const HeartbreakIcon = icon(namedIconNames.HeartbreakIcon);
export const HelpCenterIcon = icon(namedIconNames.HelpCenterIcon);
export const HelpIcon = icon(namedIconNames.HelpIcon);
export const HexagonIcon = icon(namedIconNames.HexagonIcon);
export const HidePanelIcon = icon(namedIconNames.HidePanelIcon);
export const HighHeelPanelIcon = icon(namedIconNames.HighHeelPanelIcon);
export const HighLightDuotoneIcon = icon(namedIconNames.HighLightDuotoneIcon);
export const HighLightLinearIcon = icon(namedIconNames.HighLightLinearIcon);
export const HistoryIcon = icon(namedIconNames.HistoryIcon);
export const HomeIcon = icon(namedIconNames.HomeIcon);
export const HomePanelIcon = icon(namedIconNames.HomePanelIcon);
export const ImageIcon = icon(namedIconNames.ImageIcon);
export const ImagePanelIcon = icon(namedIconNames.ImagePanelIcon);
export const ImportIcon = icon(namedIconNames.ImportIcon);
export const ImproveWritingIcon = icon(namedIconNames.ImproveWritingIcon);
export const InboxIcon = icon(namedIconNames.InboxIcon);
export const InfoIcon = icon(namedIconNames.InfoIcon);
export const InformationFillDuotoneIcon = icon(
  namedIconNames.InformationFillDuotoneIcon
);
export const InformationIcon = icon(namedIconNames.InformationIcon);
export const InformationPanelIcon = icon(namedIconNames.InformationPanelIcon);
export const InsertAboveIcon = icon(namedIconNames.InsertAboveIcon);
export const InsertBelowIcon = icon(namedIconNames.InsertBelowIcon);
export const InsertBleowIcon = icon(namedIconNames.InsertBleowIcon);
export const InsertBlocksIcon = icon(namedIconNames.InsertBlocksIcon);
export const InsertIntoPageIcon = icon(namedIconNames.InsertIntoPageIcon);
export const InsertLeftIcon = icon(namedIconNames.InsertLeftIcon);
export const InsertRightIcon = icon(namedIconNames.InsertRightIcon);
export const InsertTopIcon = icon(namedIconNames.InsertTopIcon);
export const InstagramDuotoneIcon = icon(namedIconNames.InstagramDuotoneIcon);
export const InstagramIcon = icon(namedIconNames.InstagramIcon);
export const InsyncIcon = icon(namedIconNames.InsyncIcon);
export const IntegrationsIcon = icon(namedIconNames.IntegrationsIcon);
export const InvisibleIcon = icon(namedIconNames.InvisibleIcon);
export const IssueIcon = icon(namedIconNames.IssueIcon);
export const ItalicIcon = icon(namedIconNames.ItalicIcon);
export const JournalIcon = icon(namedIconNames.JournalIcon);
export const KeyboardIcon = icon(namedIconNames.KeyboardIcon);
export const LanguageIcon = icon(namedIconNames.LanguageIcon);
export const LayerIcon = icon(namedIconNames.LayerIcon);
export const LayoutIcon = icon(namedIconNames.LayoutIcon);
export const LeafPanelIcon = icon(namedIconNames.LeafPanelIcon);
export const LeftBracketIcon = icon(namedIconNames.LeftBracketIcon);
export const LiftPanelIcon = icon(namedIconNames.LiftPanelIcon);
export const LightModeIcon = icon(namedIconNames.LightModeIcon);
export const LightPanelIcon = icon(namedIconNames.LightPanelIcon);
export const LineStyleIcon = icon(namedIconNames.LineStyleIcon);
export const LinearLogoDuotoneIcon = icon(namedIconNames.LinearLogoDuotoneIcon);
export const LinearLogoIcon = icon(namedIconNames.LinearLogoIcon);
export const LinkBlockIcon = icon(namedIconNames.LinkBlockIcon);
export const LinkIcon = icon(namedIconNames.LinkIcon);
export const LinkedEdgelessIcon = icon(namedIconNames.LinkedEdgelessIcon);
export const LinkedPageIcon = icon(namedIconNames.LinkedPageIcon);
export const LoadingIcon = icon(namedIconNames.LoadingIcon);
export const LocalDataIcon = icon(namedIconNames.LocalDataIcon);
export const LocalWorkspaceIcon = icon(namedIconNames.LocalWorkspaceIcon);
export const LocateIcon = icon(namedIconNames.LocateIcon);
export const LockIcon = icon(namedIconNames.LockIcon);
export const LockPanelIcon = icon(namedIconNames.LockPanelIcon);
export const Logo1DuotoneIcon = icon(namedIconNames.Logo1DuotoneIcon);
export const Logo1Icon = icon(namedIconNames.Logo1Icon);
export const Logo2DuotoneIcon = icon(namedIconNames.Logo2DuotoneIcon);
export const Logo2Icon = icon(namedIconNames.Logo2Icon);
export const Logo3DuotoneIcon = icon(namedIconNames.Logo3DuotoneIcon);
export const Logo3Icon = icon(namedIconNames.Logo3Icon);
export const Logo4DuotoneIcon = icon(namedIconNames.Logo4DuotoneIcon);
export const Logo4Icon = icon(namedIconNames.Logo4Icon);
export const LongSleevePanelIcon = icon(namedIconNames.LongSleevePanelIcon);
export const LongerIcon = icon(namedIconNames.LongerIcon);
export const LoomLogoDuotoneIcon = icon(namedIconNames.LoomLogoDuotoneIcon);
export const LoomLogoIcon = icon(namedIconNames.LoomLogoIcon);
export const LtIcon = icon(namedIconNames.LtIcon);
export const MailPanelIcon = icon(namedIconNames.MailPanelIcon);
export const MailboxPanelIcon = icon(namedIconNames.MailboxPanelIcon);
export const MakeItRealIcon = icon(namedIconNames.MakeItRealIcon);
export const MapPanelIcon = icon(namedIconNames.MapPanelIcon);
export const MathPanelIcon = icon(namedIconNames.MathPanelIcon);
export const McpIcon = icon(namedIconNames.McpIcon);
export const MeetingIcon = icon(namedIconNames.MeetingIcon);
export const MemberIcon = icon(namedIconNames.MemberIcon);
export const MenubarIcon = icon(namedIconNames.MenubarIcon);
export const MicPanelIcon = icon(namedIconNames.MicPanelIcon);
export const MicrophoneIcon = icon(namedIconNames.MicrophoneIcon);
export const MindmapIcon = icon(namedIconNames.MindmapIcon);
export const MindmapNodeIcon = icon(namedIconNames.MindmapNodeIcon);
export const MinusIcon = icon(namedIconNames.MinusIcon);
export const MoonPanelIcon = icon(namedIconNames.MoonPanelIcon);
export const MoreHorizontalIcon = icon(namedIconNames.MoreHorizontalIcon);
export const MoreVerticalIcon = icon(namedIconNames.MoreVerticalIcon);
export const MountainPanelIcon = icon(namedIconNames.MountainPanelIcon);
export const MousePanelIcon = icon(namedIconNames.MousePanelIcon);
export const MoveLeftIcon = icon(namedIconNames.MoveLeftIcon);
export const MoveRightIcon = icon(namedIconNames.MoveRightIcon);
export const MoveToIcon = icon(namedIconNames.MoveToIcon);
export const MoveToLeftDuotoneIcon = icon(namedIconNames.MoveToLeftDuotoneIcon);
export const MoveToRightDuotoneIcon = icon(
  namedIconNames.MoveToRightDuotoneIcon
);
export const MoviePanelIcon = icon(namedIconNames.MoviePanelIcon);
export const MultiCursorDuotoneIcon = icon(
  namedIconNames.MultiCursorDuotoneIcon
);
export const MultiPeopleIcon = icon(namedIconNames.MultiPeopleIcon);
export const MultiSelectIcon = icon(namedIconNames.MultiSelectIcon);
export const NeIcon = icon(namedIconNames.NeIcon);
export const NewIcon = icon(namedIconNames.NewIcon);
export const NewPageIcon = icon(namedIconNames.NewPageIcon);
export const NewXxxEdgelessIcon = icon(namedIconNames.NewXxxEdgelessIcon);
export const NewXxxPageIcon = icon(namedIconNames.NewXxxPageIcon);
export const NightPanelIcon = icon(namedIconNames.NightPanelIcon);
export const NoNetworkIcon = icon(namedIconNames.NoNetworkIcon);
export const NoteShadowDuotoneIcon = icon(namedIconNames.NoteShadowDuotoneIcon);
export const NotificationIcon = icon(namedIconNames.NotificationIcon);
export const NotionDuotoneIcon = icon(namedIconNames.NotionDuotoneIcon);
export const NotionIcon = icon(namedIconNames.NotionIcon);
export const NowIcon = icon(namedIconNames.NowIcon);
export const NumberIcon = icon(namedIconNames.NumberIcon);
export const NumberedListIcon = icon(namedIconNames.NumberedListIcon);
export const OctobaseIcon = icon(namedIconNames.OctobaseIcon);
export const OpenInNewIcon = icon(namedIconNames.OpenInNewIcon);
export const OtherIcon = icon(namedIconNames.OtherIcon);
export const PageIcon = icon(namedIconNames.PageIcon);
export const PaletteIcon = icon(namedIconNames.PaletteIcon);
export const ParallelogramIcon = icon(namedIconNames.ParallelogramIcon);
export const PasteIcon = icon(namedIconNames.PasteIcon);
export const PausePanelIcon = icon(namedIconNames.PausePanelIcon);
export const PaymentIcon = icon(namedIconNames.PaymentIcon);
export const PeacePanelIcon = icon(namedIconNames.PeacePanelIcon);
export const PenIcon = icon(namedIconNames.PenIcon);
export const PenThickIcon = icon(namedIconNames.PenThickIcon);
export const PenThickLeftIcon = icon(namedIconNames.PenThickLeftIcon);
export const PenThinIcon = icon(namedIconNames.PenThinIcon);
export const PeriodIcon = icon(namedIconNames.PeriodIcon);
export const PersonPanelIcon = icon(namedIconNames.PersonPanelIcon);
export const PhonePanelIcon = icon(namedIconNames.PhonePanelIcon);
export const PickerPanelIcon = icon(namedIconNames.PickerPanelIcon);
export const PillIcon = icon(namedIconNames.PillIcon);
export const PinIcon = icon(namedIconNames.PinIcon);
export const PinedIcon = icon(namedIconNames.PinedIcon);
export const PlanetPanelIcon = icon(namedIconNames.PlanetPanelIcon);
export const PlayFillIcon = icon(namedIconNames.PlayFillIcon);
export const PlayIcon = icon(namedIconNames.PlayIcon);
export const PlayPanelIcon = icon(namedIconNames.PlayPanelIcon);
export const PluginIcon = icon(namedIconNames.PluginIcon);
export const PlusIcon = icon(namedIconNames.PlusIcon);
export const PlusThickIcon = icon(namedIconNames.PlusThickIcon);
export const PptIcon = icon(namedIconNames.PptIcon);
export const Presentation2PanelIcon = icon(
  namedIconNames.Presentation2PanelIcon
);
export const PresentationIcon = icon(namedIconNames.PresentationIcon);
export const PresentationPanelIcon = icon(namedIconNames.PresentationPanelIcon);
export const PrinterIcon = icon(namedIconNames.PrinterIcon);
export const ProductHuntDuotoneIcon = icon(
  namedIconNames.ProductHuntDuotoneIcon
);
export const ProductHuntIcon = icon(namedIconNames.ProductHuntIcon);
export const ProgressIcon = icon(namedIconNames.ProgressIcon);
export const PropertyIcon = icon(namedIconNames.PropertyIcon);
export const PublishIcon = icon(namedIconNames.PublishIcon);
export const QrCodePanelIcon = icon(namedIconNames.QrCodePanelIcon);
export const QuoteIcon = icon(namedIconNames.QuoteIcon);
export const RadiantIcon = icon(namedIconNames.RadiantIcon);
export const RainPanelIcon = icon(namedIconNames.RainPanelIcon);
export const ReadwiseIcon = icon(namedIconNames.ReadwiseIcon);
export const ReadwiseLogoDuotoneIcon = icon(
  namedIconNames.ReadwiseLogoDuotoneIcon
);
export const ReauthenticationIcon = icon(namedIconNames.ReauthenticationIcon);
export const RecentIcon = icon(namedIconNames.RecentIcon);
export const RedditDuotoneIcon = icon(namedIconNames.RedditDuotoneIcon);
export const RedditIcon = icon(namedIconNames.RedditIcon);
export const RedoIcon = icon(namedIconNames.RedoIcon);
export const ReleaseFromGroupIcon = icon(namedIconNames.ReleaseFromGroupIcon);
export const RemoveFolderIcon = icon(namedIconNames.RemoveFolderIcon);
export const RemoveIcon = icon(namedIconNames.RemoveIcon);
export const ReplaceIcon = icon(namedIconNames.ReplaceIcon);
export const ResetIcon = icon(namedIconNames.ResetIcon);
export const ResizeTidyUpIcon = icon(namedIconNames.ResizeTidyUpIcon);
export const RewindFifteenSecondsIcon = icon(
  namedIconNames.RewindFifteenSecondsIcon
);
export const RightBracketIcon = icon(namedIconNames.RightBracketIcon);
export const RightLayoutIcon = icon(namedIconNames.RightLayoutIcon);
export const RightSidebarIcon = icon(namedIconNames.RightSidebarIcon);
export const RightTabIcon = icon(namedIconNames.RightTabIcon);
export const RotateAnticlockwiseIcon = icon(
  namedIconNames.RotateAnticlockwiseIcon
);
export const RotateIcon = icon(namedIconNames.RotateIcon);
export const RoundedRectangleIcon = icon(namedIconNames.RoundedRectangleIcon);
export const RoundedSquareIcon = icon(namedIconNames.RoundedSquareIcon);
export const RulerPanelIcon = icon(namedIconNames.RulerPanelIcon);
export const SadPanelIcon = icon(namedIconNames.SadPanelIcon);
export const SaveIcon = icon(namedIconNames.SaveIcon);
export const ScaleAltIcon = icon(namedIconNames.ScaleAltIcon);
export const ScaleIcon = icon(namedIconNames.ScaleIcon);
export const ScissorsIcon = icon(namedIconNames.ScissorsIcon);
export const SearchIcon = icon(namedIconNames.SearchIcon);
export const SelectAreaIcon = icon(namedIconNames.SelectAreaIcon);
export const SelectIcon = icon(namedIconNames.SelectIcon);
export const SelectTextIcon = icon(namedIconNames.SelectTextIcon);
export const SelectionIcon = icon(namedIconNames.SelectionIcon);
export const SelfhostIcon = icon(namedIconNames.SelfhostIcon);
export const SendIcon = icon(namedIconNames.SendIcon);
export const SettingsIcon = icon(namedIconNames.SettingsIcon);
export const SettingsPanelIcon = icon(namedIconNames.SettingsPanelIcon);
export const ShapeIcon = icon(namedIconNames.ShapeIcon);
export const ShareIcon = icon(namedIconNames.ShareIcon);
export const SharedIcon = icon(namedIconNames.SharedIcon);
export const ShareiOsIcon = icon(namedIconNames.ShareiOsIcon);
export const ShoesPanelIcon = icon(namedIconNames.ShoesPanelIcon);
export const ShortPantsPanelIcon = icon(namedIconNames.ShortPantsPanelIcon);
export const ShorterIcon = icon(namedIconNames.ShorterIcon);
export const SiblingNodeIcon = icon(namedIconNames.SiblingNodeIcon);
export const SidebarDuotoneIcon = icon(namedIconNames.SidebarDuotoneIcon);
export const SidebarIcon = icon(namedIconNames.SidebarIcon);
export const SignOutIcon = icon(namedIconNames.SignOutIcon);
export const SignalIcon = icon(namedIconNames.SignalIcon);
export const SignalPanelIcon = icon(namedIconNames.SignalPanelIcon);
export const SingleSelectCheckSolidIcon = icon(
  namedIconNames.SingleSelectCheckSolidIcon
);
export const SingleSelectIcon = icon(namedIconNames.SingleSelectIcon);
export const SingleSelectSelectLinearIcon = icon(
  namedIconNames.SingleSelectSelectLinearIcon
);
export const SingleSelectUnIcon = icon(namedIconNames.SingleSelectUnIcon);
export const SkateboardPanelIcon = icon(namedIconNames.SkateboardPanelIcon);
export const SkirtPanelIcon = icon(namedIconNames.SkirtPanelIcon);
export const SmileIcon = icon(namedIconNames.SmileIcon);
export const SmilePanelIcon = icon(namedIconNames.SmilePanelIcon);
export const SmileSolidIcon = icon(namedIconNames.SmileSolidIcon);
export const Snow2PanelIcon = icon(namedIconNames.Snow2PanelIcon);
export const SnowMountainPanelIcon = icon(namedIconNames.SnowMountainPanelIcon);
export const SnowPanelIcon = icon(namedIconNames.SnowPanelIcon);
export const SocketPanelIcon = icon(namedIconNames.SocketPanelIcon);
export const SoloViewIcon = icon(namedIconNames.SoloViewIcon);
export const SortDownIcon = icon(namedIconNames.SortDownIcon);
export const SortIcon = icon(namedIconNames.SortIcon);
export const SortUpIcon = icon(namedIconNames.SortUpIcon);
export const SplitViewIcon = icon(namedIconNames.SplitViewIcon);
export const SquareIcon = icon(namedIconNames.SquareIcon);
export const StarIcon = icon(namedIconNames.StarIcon);
export const StarPanelIcon = icon(namedIconNames.StarPanelIcon);
export const StartNumberingIcon = icon(namedIconNames.StartNumberingIcon);
export const StartPointArrowIcon = icon(namedIconNames.StartPointArrowIcon);
export const StartPointCircleIcon = icon(namedIconNames.StartPointCircleIcon);
export const StartPointDiamondIcon = icon(namedIconNames.StartPointDiamondIcon);
export const StartPointIcon = icon(namedIconNames.StartPointIcon);
export const StartPointTriangleIcon = icon(
  namedIconNames.StartPointTriangleIcon
);
export const StopAiIcon = icon(namedIconNames.StopAiIcon);
export const StopIcon = icon(namedIconNames.StopIcon);
export const StraightLineIcon = icon(namedIconNames.StraightLineIcon);
export const StrikeThroughIcon = icon(namedIconNames.StrikeThroughIcon);
export const StyleGeneralIcon = icon(namedIconNames.StyleGeneralIcon);
export const StyleIcon = icon(namedIconNames.StyleIcon);
export const StyleScribbleIcon = icon(namedIconNames.StyleScribbleIcon);
export const SubNodeIcon = icon(namedIconNames.SubNodeIcon);
export const SucessfulDuotoneIcon = icon(namedIconNames.SucessfulDuotoneIcon);
export const SucessfulIcon = icon(namedIconNames.SucessfulIcon);
export const SuitcasePanelIcon = icon(namedIconNames.SuitcasePanelIcon);
export const SummarizeIcon = icon(namedIconNames.SummarizeIcon);
export const SunPanelIcon = icon(namedIconNames.SunPanelIcon);
export const SunglassPanelIcon = icon(namedIconNames.SunglassPanelIcon);
export const TShirtPanelIcon = icon(namedIconNames.TShirtPanelIcon);
export const TableIcon = icon(namedIconNames.TableIcon);
export const TagIcon = icon(namedIconNames.TagIcon);
export const TagPanelIcon = icon(namedIconNames.TagPanelIcon);
export const TagsIcon = icon(namedIconNames.TagsIcon);
export const TeXDuotoneIcon = icon(namedIconNames.TeXDuotoneIcon);
export const TeXIcon = icon(namedIconNames.TeXIcon);
export const TeamWorkspaceIcon = icon(namedIconNames.TeamWorkspaceIcon);
export const TelegramDuotoneIcon = icon(namedIconNames.TelegramDuotoneIcon);
export const TelegramIcon = icon(namedIconNames.TelegramIcon);
export const TemplateColoredIcon = icon(namedIconNames.TemplateColoredIcon);
export const TemplateIcon = icon(namedIconNames.TemplateIcon);
export const TemplateOutlineIcon = icon(namedIconNames.TemplateOutlineIcon);
export const TextAlignBottomIcon = icon(namedIconNames.TextAlignBottomIcon);
export const TextAlignCenterIcon = icon(namedIconNames.TextAlignCenterIcon);
export const TextAlignLeftIcon = icon(namedIconNames.TextAlignLeftIcon);
export const TextAlignMediumIcon = icon(namedIconNames.TextAlignMediumIcon);
export const TextAlignRightIcon = icon(namedIconNames.TextAlignRightIcon);
export const TextAlignTopIcon = icon(namedIconNames.TextAlignTopIcon);
export const TextBackgroundDuotoneIcon = icon(
  namedIconNames.TextBackgroundDuotoneIcon
);
export const TextColorIcon = icon(namedIconNames.TextColorIcon);
export const TextIcon = icon(namedIconNames.TextIcon);
export const TextTypeIcon = icon(namedIconNames.TextTypeIcon);
export const ThinkingIcon = icon(namedIconNames.ThinkingIcon);
export const TimePanelIcon = icon(namedIconNames.TimePanelIcon);
export const TimelineIcon = icon(namedIconNames.TimelineIcon);
export const TitleIcon = icon(namedIconNames.TitleIcon);
export const TocIcon = icon(namedIconNames.TocIcon);
export const TodayIcon = icon(namedIconNames.TodayIcon);
export const ToggleDownIcon = icon(namedIconNames.ToggleDownIcon);
export const ToggleLeftIcon = icon(namedIconNames.ToggleLeftIcon);
export const ToggleRightIcon = icon(namedIconNames.ToggleRightIcon);
export const ToggleUpIcon = icon(namedIconNames.ToggleUpIcon);
export const TomorrowIcon = icon(namedIconNames.TomorrowIcon);
export const ToneIcon = icon(namedIconNames.ToneIcon);
export const ToolIcon = icon(namedIconNames.ToolIcon);
export const TranscriptWithAiIcon = icon(namedIconNames.TranscriptWithAiIcon);
export const TreePanelIcon = icon(namedIconNames.TreePanelIcon);
export const TriangleIcon = icon(namedIconNames.TriangleIcon);
export const TriangleRulerPanelIcon = icon(
  namedIconNames.TriangleRulerPanelIcon
);
export const TwitterDuotoneIcon = icon(namedIconNames.TwitterDuotoneIcon);
export const TwitterIcon = icon(namedIconNames.TwitterIcon);
export const UmbrellaPanelIcon = icon(namedIconNames.UmbrellaPanelIcon);
export const UnderLineIcon = icon(namedIconNames.UnderLineIcon);
export const UndoIcon = icon(namedIconNames.UndoIcon);
export const UngroupIcon = icon(namedIconNames.UngroupIcon);
export const UnlinkIcon = icon(namedIconNames.UnlinkIcon);
export const UnlockIcon = icon(namedIconNames.UnlockIcon);
export const UnpinIcon = icon(namedIconNames.UnpinIcon);
export const UnsucessfulDuotoneIcon = icon(
  namedIconNames.UnsucessfulDuotoneIcon
);
export const UnsucessfulIcon = icon(namedIconNames.UnsucessfulIcon);
export const UnsyncIcon = icon(namedIconNames.UnsyncIcon);
export const UpdatedIcon = icon(namedIconNames.UpdatedIcon);
export const UpgradeIcon = icon(namedIconNames.UpgradeIcon);
export const UploadIcon = icon(namedIconNames.UploadIcon);
export const UsbPanelIcon = icon(namedIconNames.UsbPanelIcon);
export const UserGuideIcon = icon(namedIconNames.UserGuideIcon);
export const UserIcon = icon(namedIconNames.UserIcon);
export const VerticalCylinderIcon = icon(namedIconNames.VerticalCylinderIcon);
export const ViewBarIcon = icon(namedIconNames.ViewBarIcon);
export const ViewIcon = icon(namedIconNames.ViewIcon);
export const ViewLayersIcon = icon(namedIconNames.ViewLayersIcon);
export const VoiceIcon = icon(namedIconNames.VoiceIcon);
export const VolumePanelIcon = icon(namedIconNames.VolumePanelIcon);
export const WalletPanelIcon = icon(namedIconNames.WalletPanelIcon);
export const WarningIcon = icon(namedIconNames.WarningIcon);
export const WaveRectangleIcon = icon(namedIconNames.WaveRectangleIcon);
export const WebIcon = icon(namedIconNames.WebIcon);
export const WindmillPanelIcon = icon(namedIconNames.WindmillPanelIcon);
export const WindowPanelIcon = icon(namedIconNames.WindowPanelIcon);
export const WrapIcon = icon(namedIconNames.WrapIcon);
export const YesterdayIcon = icon(namedIconNames.YesterdayIcon);
export const YoutubeDuotoneIcon = icon(namedIconNames.YoutubeDuotoneIcon);
export const YoutubeIcon = icon(namedIconNames.YoutubeIcon);
export const YoutubePanelIcon = icon(namedIconNames.YoutubePanelIcon);
export const ZendeskDuotoneIcon = icon(namedIconNames.ZendeskDuotoneIcon);
export const ZendeskIcon = icon(namedIconNames.ZendeskIcon);
export const ZipIcon = icon(namedIconNames.ZipIcon);
export const ZoomDownIcon = icon(namedIconNames.ZoomDownIcon);
export const ZoomUpIcon = icon(namedIconNames.ZoomUpIcon);
export const DashboardIcon = icon(namedIconNames.DashboardIcon);
export const TodoListIcon = icon(namedIconNames.TodoListIcon);
export const DatabaseIcon = icon(namedIconNames.DatabaseIcon);
export const ChevronLeftIcon = icon(namedIconNames.ChevronLeftIcon);
export const ChevronRightIcon = icon(namedIconNames.ChevronRightIcon);
export const ChevronDownIcon = icon(namedIconNames.ChevronDownIcon);
export const ChevronRightMediumIcon = icon(
  namedIconNames.ChevronRightMediumIcon
);
